import { ComponentTypesV2, PartnerType } from 'opensolar-common'

export interface HighlightConfig {
  id: string
  title: string
  description: string
  cta: {
    label: string
    type: 'url' | 'file'
    url?: string
    file: any
    onClick?: () => void
  }
  enableCountdown: boolean
  countdownDate?: string
  fullWidth: boolean
  whiteText: boolean
  alignTextCenter?: boolean | undefined
  ui3styling?: boolean | undefined
  show_brand_logo?: boolean | undefined
  backgroundUrl?: string | null
  brand_logo_url?: string | undefined
  enablePromoImage?: boolean | undefined
  backgroundColor?: string | undefined
}

export interface BlogConfig {
  id: string
  title: string
  url: string
  author: string
  image: string
  publish_date: string
}

export interface VideoData {
  title: string
  thumbnail_url: string
  video_url: string
}

interface HighlightsElementSchema {
  contentType: 'highlights'
  enabled: boolean
  data?: HighlightConfig[]
}

interface NavbarCTASchema {
  label?: string
  link?: string
  newTab?: boolean
}

interface NavbarElementSchema {
  contentType: 'navbar'
  enabled: boolean
  data?: {
    cta?: NavbarCTASchema
    secondaryCta?: NavbarCTASchema
  }
}

interface CoverElementSchema {
  contentType: 'cover'
  enabled: boolean
  data?: {
    tagline: string
    whiteText?: boolean
  }
}

interface OurStoryElementSchema {
  contentType: 'our_story'
  enabled: boolean
  data?: {
    body: string
    video?: string
  }
}

interface StatsElementSchema {
  contentType: 'stats'
  enabled: boolean
  data?: {
    id: string
    value: string
    description: string
  }[]
}

interface SocialsElementSchema {
  contentType: 'socials'
  enabled: boolean
  data?: {
    widgetLink: string
  }
}

interface UrlData {
  id: string
  url: string
}

interface VideosElementSchema {
  contentType: 'videos'
  enabled: boolean
  data?: UrlData[]
}

interface BlogsElementSchema {
  contentType: 'blogs'
  enabled: boolean
  data?: UrlData[]
}

interface ResourcesElementSchema {
  contentType: 'resources'
  enabled: boolean
  data?: UrlData
}

interface CustomContentData {
  navHeading: string
  content: string
}

interface CustomContent1ElementSchema {
  contentType: 'custom_content_1'
  enabled: boolean
  data?: CustomContentData
}

interface CustomContent2ElementSchema {
  contentType: 'custom_content_2'
  enabled: boolean
  data?: CustomContentData
}

interface CustomContent3ElementSchema {
  contentType: 'custom_content_3'
  enabled: boolean
  data?: CustomContentData
}

interface FeaturedProductsElementSchema {
  contentType: 'featured_products'
  enabled: boolean
  data?: {
    component_type: ComponentTypesV2[]
  }
}

interface ChatElementSchema {
  contentType: 'chat'
  enabled: boolean
  data?: {
    apiKey: string
  }
}

export type ContentBlock =
  | HighlightsElementSchema
  | NavbarElementSchema
  | CoverElementSchema
  | OurStoryElementSchema
  | StatsElementSchema
  | SocialsElementSchema
  | VideosElementSchema
  | BlogsElementSchema
  | ResourcesElementSchema
  | CustomContent1ElementSchema
  | CustomContent2ElementSchema
  | CustomContent3ElementSchema
  | FeaturedProductsElementSchema
  | ChatElementSchema

export interface HighlightAsset {
  backgroundImage?: string
  promoImage?: string
  ctaFile?: string
}

export interface AssetsConfig {
  cover_background?: string
  highlights?: Record<string, HighlightAsset>
}

export interface ContentsConfig {
  elements: ContentBlock[]
  assets: AssetsConfig
}

export interface ExpoStand {
  url?: string
  id: number
  title: string
  description: string
  locale: string
  contents: ContentsConfig
  manufacturer: string
  tags: string[]
  countries_include: string
  countries_exclude: string
  brand_content: {
    name: string
    logo_contents: any
    slug: string
    partner_type: PartnerType
    manufacturers: string[]
    hardware_suppliers?: string[]
  }
}

export interface StandData {
  org: {
    id: number
    name: string
    premium_expo: boolean
    logo_public_url: string | null
  }
  expoStand: ExpoStand
}

export type ConfigType = {
  label: string
  toggleEnabled: boolean
  reorderable: boolean
  required: boolean
  premium: boolean
  navLabel?: string
}

export interface ExpoState {
  selectedCountry?: string | null
  selectedLocale?: string | null
  isCountryOverrideEnabled: boolean
  brandSlug?: string | undefined
}

export type ExpoPathState = Pick<ExpoState, 'selectedCountry' | 'selectedLocale' | 'brandSlug'>

export const COMPONENT_CONFIG: { [id: string]: ConfigType } = {
  navbar: {
    label: 'Navbar',
    toggleEnabled: false,
    reorderable: false,
    required: true,
    premium: false,
  },
  cover: {
    label: 'Cover Image',
    toggleEnabled: false,
    reorderable: false,
    required: false,
    premium: true,
  },
  our_story: {
    label: 'Our Story',
    toggleEnabled: false,
    reorderable: false,
    required: true,
    navLabel: 'Home',
    premium: false,
  },

  highlights: {
    label: 'Highlights',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  stats: {
    label: 'Why Us',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    navLabel: 'Why Us',
    premium: true,
  },
  socials: {
    label: 'Socials',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  videos: {
    label: 'Videos',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  blogs: {
    label: 'Blogs',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  resources: {
    label: 'Resources',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  custom_content_1: {
    label: 'Custom Content 1',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  custom_content_2: {
    label: 'Custom Content 2',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  custom_content_3: {
    label: 'Custom Content 3',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    premium: true,
  },
  featured_products: {
    label: 'Featured Products (Hardware)',
    toggleEnabled: true,
    reorderable: true,
    required: false,
    navLabel: 'Shop',
    premium: false,
  },
  chat: {
    label: 'Chat',
    toggleEnabled: true,
    reorderable: false,
    required: false,
    premium: true,
  },
}

export type ExpoLoggerType = (data: Object) => void
